import { db, makeId } from "./db";
import type {
  ExerciseDefinition,
  Program,
  ProgressionMode,
  TrackingMode,
  WorkoutTemplate,
  WorkoutTemplateExercise,
} from "./types";

const now = () => Date.now();
const slug = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

type SeedExercise = {
  name: string;
  sets: number;
  minSets?: number;
  reps?: [number, number];
  duration?: [number, number];
  rir?: [number, number];
  rest: number;
  unilateral?: boolean;
  load?: number;
  increment?: number;
  mode?: ProgressionMode;
  tracking?: TrackingMode;
};

const sessions: Array<{ name: string; exercises: SeedExercise[] }> = [
  {
    name: "A — Lourd",
    exercises: [
      { name: "BSS haltères", sets: 4, reps: [5, 7], rir: [1, 2], rest: 180, unilateral: true, load: 24, increment: 2 },
      { name: "Tractions lestées", sets: 4, reps: [4, 6], rir: [1, 2], rest: 180, load: 20, increment: 2.5 },
      { name: "Dips lestés", sets: 4, reps: [5, 7], rir: [1, 2], rest: 180, load: 25, increment: 2.5 },
      { name: "RDL haltères", sets: 3, reps: [6, 8], rir: [1, 2], rest: 180, load: 34, increment: 2 },
      { name: "Élévations latérales", sets: 3, reps: [12, 20], rir: [0, 1], rest: 75, load: 8, increment: 1 },
      { name: "Copenhagen plank", sets: 2, duration: [30, 45], rest: 75, unilateral: true, tracking: "duration", mode: "duration" },
      { name: "Grip lourd", sets: 4, reps: [3, 5], rest: 150, mode: "discrete", load: 2, increment: 1 },
    ],
  },
  {
    name: "B — Moyen",
    exercises: [
      { name: "RDL haltères", sets: 4, reps: [6, 10], rir: [1, 2], rest: 150, load: 30, increment: 2 },
      { name: "Dips lestés", sets: 3, reps: [8, 10], rir: [1, 2], rest: 150, load: 17.5, increment: 2.5 },
      { name: "Tractions lestées", sets: 3, reps: [7, 10], rir: [1, 2], rest: 150, load: 12.5, increment: 2.5 },
      { name: "Fentes arrière haltères", sets: 3, reps: [8, 12], rir: [1, 2], rest: 120, unilateral: true, load: 20, increment: 2 },
      { name: "Rowing unilatéral", sets: 3, reps: [8, 12], rir: [1, 1], rest: 120, unilateral: true, load: 30, increment: 2 },
      { name: "Curl haltères", sets: 3, reps: [10, 15], rir: [0, 1], rest: 75, load: 12, increment: 1 },
      { name: "Extension triceps élastique au-dessus de la tête", sets: 3, reps: [12, 20], rir: [0, 1], rest: 75, mode: "discrete" },
      { name: "Face pulls élastique", sets: 3, reps: [15, 25], rir: [0, 1], rest: 60, mode: "discrete" },
    ],
  },
  {
    name: "C — Volume",
    exercises: [
      { name: "BSS haltères", sets: 3, reps: [10, 15], rir: [1, 2], rest: 120, unilateral: true, load: 18, increment: 2 },
      { name: "Pompes lestées ou élastique", sets: 3, reps: [10, 20], rir: [1, 1], rest: 120, load: 10, increment: 2.5 },
      { name: "Rowing haltères", sets: 4, reps: [10, 15], rir: [1, 1], rest: 120, load: 24, increment: 2 },
      { name: "Goblet squat", sets: 2, reps: [12, 20], rir: [1, 1], rest: 120, load: 30, increment: 2 },
      { name: "Pullover haltère ou tirage bras tendus élastique", sets: 3, reps: [12, 20], rir: [1, 1], rest: 90, load: 16, increment: 2 },
      { name: "Élévations latérales", minSets: 2, sets: 3, reps: [15, 25], rir: [0, 1], rest: 75, load: 8, increment: 1 },
      { name: "Face pulls", sets: 2, reps: [15, 25], rir: [0, 1], rest: 60, mode: "discrete" },
      { name: "Grip volume", sets: 3, reps: [6, 10], rir: [1, 1], rest: 120, mode: "discrete" },
    ],
  },
];

const BUNDLED_PROGRAM_VERSION = 2;

async function upgradeBundledProgram() {
  const settings = await db.settings.get("settings");
  if (!settings || (settings.bundledProgramVersion ?? 0) >= BUNDLED_PROGRAM_VERSION) return;
  const program = await db.programs.filter((item) => item.isCurrent && !item.archived).first();
  if (!program) {
    await db.settings.update("settings", { bundledProgramVersion: BUNDLED_PROGRAM_VERSION });
    return;
  }
  const templates = (await db.templates.bulkGet(program.templateIds)).filter(Boolean) as WorkoutTemplate[];
  const sessionB = templates.find((item) => item.name === "B — Moyen");
  const sessionC = templates.find((item) => item.name === "C — Volume");
  if (!sessionB || !sessionC) {
    await db.settings.update("settings", { bundledProgramVersion: BUNDLED_PROGRAM_VERSION });
    return;
  }

  const definitions = await db.exercises.toArray();
  const definitionByName = new Map(definitions.map((item) => [item.name, item]));
  const changedAt = now();
  let triceps = definitionByName.get("Extension triceps élastique au-dessus de la tête");
  if (!triceps) {
    triceps = {
      id: makeId("exercise_extension_triceps_elastique"),
      name: "Extension triceps élastique au-dessus de la tête",
      archived: false,
      isUnilateral: false,
      perSide: false,
      trackingMode: "reps",
      progressionMode: "discrete",
      createdAt: changedAt,
      updatedAt: changedAt,
    };
  }
  const elevations = definitionByName.get("Élévations latérales");
  const curl = definitionByName.get("Curl haltères");
  const facePulls = definitionByName.get("Face pulls");

  await db.transaction("rw", [db.exercises, db.templates, db.settings], async () => {
    if (!(await db.exercises.get(triceps.id))) await db.exercises.add(triceps);

    const bExercises = structuredClone(sessionB.exercises);
    const curlIndex = curl ? bExercises.findIndex((item) => item.exerciseDefinitionId === curl.id) : -1;
    if (curlIndex >= 0) bExercises[curlIndex].setCount = 3;
    const tricepsIndex = bExercises.findIndex((item) => item.exerciseDefinitionId === triceps.id);
    const tricepsPrescription: WorkoutTemplateExercise = {
      id: makeId("template_exercise_triceps"), exerciseDefinitionId: triceps.id,
      setCount: 3, minReps: 12, maxReps: 20, targetRirMin: 0, targetRirMax: 1,
      restSeconds: 75, load: 0, unit: "kg", loadIncrement: 1,
      discreteSteps: ["Niveau 1", "Niveau 2", "Niveau 3", "Niveau 4"], discreteStepIndex: 1,
      position: Math.max(0, curlIndex + 1),
    };
    if (tricepsIndex >= 0) bExercises[tricepsIndex] = { ...bExercises[tricepsIndex], ...tricepsPrescription, id: bExercises[tricepsIndex].id };
    else bExercises.splice(Math.max(0, curlIndex + 1), 0, tricepsPrescription);
    bExercises.forEach((item, position) => item.position = position);

    const cExercises = structuredClone(sessionC.exercises);
    if (elevations) {
      const existingIndex = cExercises.findIndex((item) => item.exerciseDefinitionId === elevations.id);
      const facePullsIndex = facePulls ? cExercises.findIndex((item) => item.exerciseDefinitionId === facePulls.id) : -1;
      const insertionIndex = facePullsIndex >= 0 ? facePullsIndex : cExercises.length;
      const elevationPrescription: WorkoutTemplateExercise = {
        id: makeId("template_exercise_elevations"), exerciseDefinitionId: elevations.id,
        minSetCount: 2, setCount: 3, minReps: 15, maxReps: 25, targetRirMin: 0, targetRirMax: 1,
        restSeconds: 75, load: 8, unit: "kg", loadIncrement: 1, position: insertionIndex,
      };
      if (existingIndex >= 0) cExercises[existingIndex] = { ...cExercises[existingIndex], ...elevationPrescription, id: cExercises[existingIndex].id };
      else cExercises.splice(insertionIndex, 0, elevationPrescription);
      cExercises.forEach((item, position) => item.position = position);
    }

    await db.templates.update(sessionB.id, { exercises: bExercises, updatedAt: changedAt });
    if (elevations) await db.templates.update(sessionC.id, { exercises: cExercises, updatedAt: changedAt });
    await db.settings.update("settings", { bundledProgramVersion: BUNDLED_PROGRAM_VERSION });
  });
}

export async function initializeDatabase() {
  await db.transaction("rw", db.settings, async () => {
    const existingSettings = await db.settings.get("settings");
    if (!existingSettings) {
      const createdAt = Date.now();
      await db.settings.add({
        id: "settings",
        darkMode: false,
        soundEnabled: true,
        vibrationEnabled: true,
        volume: 0.7,
        defaultUnit: "kg",
        backupReminderSessions: 10,
        backupReminderDays: 7,
        backupReminderStartedAt: createdAt,
        sessionsSinceLastBackup: 0,
        onboardingComplete: false,
      });
    } else if (existingSettings.vibrationEnabled == null || existingSettings.backupReminderDays == null || existingSettings.backupReminderStartedAt == null) {
      await db.settings.update("settings", {
        vibrationEnabled: existingSettings.vibrationEnabled ?? true,
        backupReminderDays: existingSettings.backupReminderDays ?? 7,
        backupReminderStartedAt: existingSettings.backupReminderStartedAt ?? existingSettings.lastBackupAt ?? Date.now(),
      });
    }
  });
  await db.transaction("rw", [db.settings, db.programs, db.templates, db.exercises], upgradeBundledProgram);
}

export async function installFullBodyTemplate(name = "Full Body — 3 jours") {
  const createdAt = now();
  const uniqueNames = [...new Set(sessions.flatMap((session) => session.exercises.map((exercise) => exercise.name)))];
  const definitions: ExerciseDefinition[] = uniqueNames.map((name) => {
    const sample = sessions.flatMap((session) => session.exercises).find((exercise) => exercise.name === name)!;
    return {
      id: makeId(`exercise_${slug(name)}`),
      name,
      archived: false,
      isUnilateral: Boolean(sample.unilateral),
      perSide: Boolean(sample.unilateral),
      trackingMode: sample.tracking ?? "reps",
      progressionMode: sample.mode ?? "double_progression",
      createdAt,
      updatedAt: createdAt,
    };
  });
  const definitionMap = new Map(definitions.map((definition) => [definition.name, definition]));
  const programId = makeId("program");
  const templates: WorkoutTemplate[] = sessions.map((session, sessionIndex) => {
    const exercises: WorkoutTemplateExercise[] = session.exercises.map((exercise, position) => ({
      id: makeId(`template_exercise_${sessionIndex}_${position}`),
      exerciseDefinitionId: definitionMap.get(exercise.name)!.id,
      minSetCount: exercise.minSets,
      setCount: exercise.sets,
      minReps: exercise.reps?.[0],
      maxReps: exercise.reps?.[1],
      minDuration: exercise.duration?.[0],
      maxDuration: exercise.duration?.[1],
      targetRirMin: exercise.rir?.[0],
      targetRirMax: exercise.rir?.[1],
      restSeconds: exercise.rest,
      load: exercise.load ?? 0,
      unit: "kg",
      loadIncrement: exercise.increment ?? 1,
      discreteSteps: exercise.mode === "discrete" ? ["Niveau 1", "Niveau 2", "Niveau 3", "Niveau 4"] : undefined,
      discreteStepIndex: exercise.mode === "discrete" ? 1 : undefined,
      position,
    }));
    return {
      id: makeId(`template_${sessionIndex + 1}`),
      programId,
      name: session.name,
      exercises,
      createdAt,
      updatedAt: createdAt,
    };
  });
  const program: Program = {
    id: programId,
    name,
    templateIds: templates.map((template) => template.id),
    archived: false,
    isCurrent: true,
    createdAt,
    updatedAt: createdAt,
  };
  await db.transaction("rw", [db.exercises, db.templates, db.programs, db.settings], async () => {
    await db.programs.toCollection().modify({ isCurrent: false });
    await db.exercises.bulkAdd(definitions);
    await db.templates.bulkAdd(templates);
    await db.programs.add(program);
    await db.settings.update("settings", { onboardingComplete: true, bundledProgramVersion: BUNDLED_PROGRAM_VERSION });
  });
  return program;
}

/** Test helper: initializes settings then installs the bundled optional template. */
export async function seedDatabase() {
  await initializeDatabase();
  if ((await db.programs.count()) === 0) await installFullBodyTemplate();
}
